
import { MessageType, PredefinedOptionType, PredefinedPreSupportOptions, SystemEvent } from '@/common/bot/enum';
import { randomId } from '@/common/bot/lib';
import { ActionSelectedEvent, ArticleAction, BaseSystemMessage, PreSupportSelectionEvent } from '@/common/bot/types';
import CustomerSupportIcon from '@/components/icons/CustomerSupportIcon.vue';
import { Component, Prop, Vue } from 'vue-property-decorator';

const ARTICLE_ACTION_ID = 'article-action' as const;

@Component
export default class PreSupportPromptMessage extends Vue {
  @Prop({ default: () => ({}) }) readonly message!: BaseSystemMessage;

  get actionItems () {
    const { type, config: { connect, action: actionConfig } = {} } = this.message;

    const restartAction = {
      id: PredefinedOptionType.RESTART_CHAT,
      message: this.$t('chat.sys.navigation.restart_here'),
      buttonText: this.$t('chat.sys.navigation.restart_chat'),
    };

    const startAgainAction = {
      id: PredefinedOptionType.RESTART_CHAT,
      message: this.$t('chat.sys.navigation.restart_here'),
      buttonText: this.$t('chat.sys.navigation.start_again'),
    };

    const endAction = {
      id: PredefinedOptionType.END_CHAT,
      message: this.$t('chat.sys.navigation.end_chat_here'),
      buttonText: this.$t('global.end_chat'),
    };

    const closeChatAction = {
      id: PredefinedOptionType.END_CHAT,
      message: this.$t('chat.sys.navigation.close_chat_here'),
      buttonText: this.$t('global.end_chat'),
    };

    const contactSupportAction = {
      id: PredefinedOptionType.CONNECT_TO_SUPPORT,
      message: this.$t('chat.sys.msg.pre_support_prompt.iff_still_issues'), // Not connect to support since we have AI flow
      buttonText: this.$t('chat.sys.msg.pre_support_prompt.need_more_help'),
      icon: CustomerSupportIcon,
    };

    const exploreTopicsAction = {
      id: PredefinedOptionType.BROWSE_OTHER_ARTICLES,
      message: this.$t('chat.sys.msg.pre_support_prompt.or_explore_other_topics'),
      buttonText: this.$t('chat.sys.msg.pre_support_prompt.explore_other_topics'),
    };

    const needMoreHelpAction = {
      id: PredefinedOptionType.NEED_MORE_HELP,
      message: this.$t('chat.sys.msg.pre_support_prompt.or_need_more_help'),
      buttonText: this.$t('chat.sys.msg.pre_support_prompt.need_more_help'),
    };

    const articleAction = actionConfig && {
      id: ARTICLE_ACTION_ID,
      message: actionConfig.actionMessage,
      buttonText: actionConfig.actionTitle || '',
    };

    const searchArticlesAction = {
      id: PredefinedOptionType.SEARCH_ARTICLES,
      message: this.$t('chat.sys.msg.pre_support_prompt.or_search_articles'),
      buttonText: this.$t('chat.sys.msg.pre_support_prompt.search_articles'),
    };

    const createBreak = () => ({
      id: randomId(),
      isBlankSpace: true,
    });

    if (type === MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY) {
      return [
        createBreak(),
        createBreak(),
        articleAction,
        restartAction,
        endAction,
      ];
    }
    if (type === MessageType.PRE_SUPPORT_PROMPT_ACTION_WITH_SUPPORT) {
      return [
        createBreak(),
        createBreak(),
        articleAction,
        createBreak(),
        contactSupportAction,
        exploreTopicsAction,
      ];
    }
    if (type === MessageType.PRE_SUPPORT_PROMPT_BARE) {
      return [
        ...(this.message.message ? [createBreak(), createBreak()] : []),
        restartAction,
        endAction,
      ];
    }
    if (type === MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION) {
      return [
        createBreak(),
        createBreak(),
        startAgainAction,
        searchArticlesAction,
        closeChatAction,
      ];
    }
    if (type === MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION_WITH_SUPPORT) {
      return [
        createBreak(),
        createBreak(),
        startAgainAction,
        searchArticlesAction,
        contactSupportAction,
        closeChatAction,
      ];
    }
    if (type === MessageType.PRE_SUPPORT_PROMPT_METADATA_ONLY) {
      return [
        createBreak(),
        createBreak(),
        needMoreHelpAction,
        restartAction,
        endAction,
      ];
    }
  }

  buildEvent (button: {
    id: PredefinedPreSupportOptions | typeof ARTICLE_ACTION_ID;
    message: string;
    buttonText: string;
    actionConfig: ArticleAction
  }) {
    if (button.id === ARTICLE_ACTION_ID) {
      const event: ActionSelectedEvent = {
        type: SystemEvent.ACTION_SELECTED,
        message: this.message,
        payload: { action: this.message.config?.action! },
      };
      this.$emit('send:event', event);
      return;
    }

    const event: PreSupportSelectionEvent = {
      type: SystemEvent.PRE_SUPPORT_SELECTION,
      message: this.message,
      payload: {
        selected: button.id,
      },
    };

    this.$emit('send:event', event);
  }
}
